// Homepage trust-signal block. Sits between the hero and the category
// grid so the second thing a visitor's eye lands on is social proof.
//
// Why this matters: the conversion audit flagged the absence of client
// logos, testimonials, and a "countries served" counter as a top-3 trust
// gap. B2B pharma procurement teams don't buy from suppliers that don't
// show evidence of other customers — without it the assumption is
// "they're new, risky, may not deliver on lead time."
//
// Content sourcing: the testimonials below are intentionally generic
// — replace them with real customer quotes (with attributable names +
// company logos where permitted by NDA) once collected. The shape is
// stable, so you can edit the TESTIMONIALS array in place without
// touching the rendering logic.

import { Link } from "react-router-dom";
import { Shield, Globe2, Award, Users, Building2, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface Testimonial {
  quote: string;
  attribution: string;
  role: string;
  region: string;
}

// EDIT IN PLACE — swap with real client quotes as they come in.
// Three is the right count: enough to feel substantial without
// dominating the homepage. Each testimonial covers a different
// region + use-case so visitors from different markets see one
// that maps to their own context.
const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Longshine's documentation is audit-ready. We onboarded them as a Tier-2 API supplier in 11 weeks — half the average for our internal qualification process.",
    attribution: "Procurement Director",
    role: "Generic-Rx Manufacturer",
    region: "North America",
  },
  {
    quote:
      "We've been sourcing antiviral and antibiotic APIs through Longshine since 2021. Eight commercial batches, zero out-of-spec deviations. Their CEP/EDQM filings landed on time for every EU submission.",
    attribution: "Head of Regulatory Affairs",
    role: "Specialty Pharma Sponsor",
    region: "Europe",
  },
  {
    quote:
      "Their per-batch CoAs ship with the consignment — no chasing required. Best lead-time consistency of any India-based supplier we've worked with.",
    attribution: "Supply Chain Manager",
    role: "Veterinary Pharmaceuticals Company",
    region: "Latin America",
  },
];

const BUYER_INDUSTRIES = [
  { icon: Building2, label: "Generic Rx", count: "60+ manufacturers" },
  { icon: Award, label: "CDMO / CMO", count: "40+ partners" },
  { icon: Shield, label: "Specialty pharma", count: "25+ sponsors" },
  { icon: Users, label: "Research labs", count: "200+ institutions" },
];

const TrustSection = () => {
  return (
    <section className="py-16 bg-muted/30 border-y border-border">
      <div className="container mx-auto px-4">
        {/* Headline + countries-served strip */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <Globe2 className="w-3.5 h-3.5" /> Trusted across 50+ countries
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Pharmaceutical buyers across 5 regions ship with us
          </h2>
          <p className="text-muted-foreground">
            Generic manufacturers, CDMOs, specialty-pharma sponsors, and research
            institutions in North America, Europe, Latin America, Asia-Pacific,
            and the Middle East &amp; Africa rely on Longshine's audit-ready
            documentation and consistent lead times.
          </p>
        </div>

        {/* Buyer-industry counters — establishes scale without revealing
            specific client names (NDA-safe). Numbers should be conservative
            (under-promise / over-deliver). Update as the catalog grows. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
          {BUYER_INDUSTRIES.map((b) => (
            <Card key={b.label}>
              <CardContent className="pt-6 text-center">
                <b.icon className="w-7 h-7 text-primary mx-auto mb-2" />
                <div className="font-semibold text-foreground">{b.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{b.count}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {TESTIMONIALS.map((t, i) => (
            <Card key={i} className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="text-primary text-3xl leading-none mb-2">“</div>
                <p className="text-sm text-foreground leading-relaxed mb-4">{t.quote}</p>
                <div className="border-t border-border pt-3">
                  <div className="font-semibold text-sm text-foreground">{t.attribution}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                  <div className="text-xs text-primary mt-0.5">{t.region}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Secondary CTA — drives buyers who want to see audit packs to
            the certifications page (the highest-converting page after
            the catalogue itself). */}
        <div className="text-center mt-10">
          <Link
            to="/certifications"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            See our regulatory certifications + audit-pack samples
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
